import { ActivityTracker, NamespaceLoggerAdapter, sendErrorEvent, sendTelemetryEvent } from '@fluidx/telemetry';
import { fetchWithRetry, safeLocalStorage } from '@fluidx/utilities';
import { generateEcsFetchUrl, getSettingLocator, getSettingOverrideFromLocalStorage, getSettingOverrideFromQueryParam, ecsResponseIsStillValid } from './utilities';
import { EcsSettingSource } from './interfaces';
import { cacheVersion, ecsClientRetryPolicy, guestAgentPattern } from './EcsClientConstants';
/**
 * Handles communication with Experimentation and Configuration Service.
 */
export class EcsClient {
    /**
     * EcsClient constructor
     * @param ecsClientConfiguration Configuration used to connect with a specific ECS account.
     * @param logger Logger used for telemetry. We create a new `NamespaceLoggerAdapter` based on this.
     */
    constructor(ecsClientConfiguration, logger) {
        this.ecsClientConfiguration = ecsClientConfiguration;
        /**
         * The first time a setting is accessed, it's stored in this map for later consumption.
         * This ensures that a setting doesn't change for the life of the `EcsClient`, and it stores
         * the value and the source of a specific setting. The map is keyed off of the setting name.
         *
         * Note that the setting name is itself a combination of a `teamName` (example: `'FluidExperiences'` )
         * and `settingName` (example: `'exampleSetting'` ).  We need to use a combination of `teamName` and `settingName`
         * due to a single response possibly containing settings for multiple teams.
         * Key for `'exampleSetting'`: `JSON.stringify({ teamName: 'FluidExperiences', settingName: 'exampleSetting' })`
         */
        this.previouslyAccessedEcsSettings = new Map();
        // Creating a new `NamespaceLoggerAdapter` based on the logger passed in the constructor.
        this.logger = logger && new NamespaceLoggerAdapter('EcsClient', logger);
        this.loadAndClearCache();
    }
    /**
     * Fetch configurations from ECS and stores them in cache to be accessed in following loads.
     */
    async fetch() {
        // We fetch if:
        //    1. There is no cached EcsResponse
        //    2. The cached EcsResponse is expired
        //    3. The sessionId of cached EcsResponse is different than the current sessionId
        if (this.ecsResponseFromCache === undefined ||
            !ecsResponseIsStillValid(this.ecsResponseFromCache) ||
            this.ecsResponseFromCache.sessionId !== this.ecsClientConfiguration.sessionId) {
            // generating url to fetch based in `EcsClientConfiguration`
            const ecsFetchUrl = generateEcsFetchUrl(this.ecsClientConfiguration);
            const ecsResponsePromise = this.fetchAndParse(ecsFetchUrl);
            ecsResponsePromise
                .then((ecsResponse) => {
                // If ecsResponse is defined, we store it in cache so next time we load we can use it and avoid network calls.
                ecsResponse && this.cacheEcsResponse(ecsResponse);
            })
                .catch(() => {
                // this situation shouldn't happen because fetchAndParse is ensuring to not throw and logs errors in the ecsFetch Activity.
            });
            return ecsResponsePromise;
        }
        return undefined;
    }
    /**
     * Gets the value of a setting.
     * @param settingName Name of the desired setting.
     * @param defaultValue Default value in case the setting is not found.
     * @param teamName Name of the ECS team to read the setting from.
     * @returns the correct setting value. Priority: Previously Accessed Value > Query Param Override > LocalStorage Override > Cached `EcsResponse` > `defaultValue`.
     */
    getSetting(settingName, defaultValue, teamName) {
        const settingLocator = getSettingLocator(teamName, settingName);
        const ecsSetting = this.getSettingInternal(settingLocator, defaultValue);
        return ecsSetting.settingValue;
    }
    /**
     * Gets setting from all the different source available with priority.
     * Priority: Previously Accessed Value > Query Param Override > LocalStorage Override > Cached `EcsResponse` > `defaultValue`.
     * @returns the value and source of the specified setting
     */
    getSettingInternal(settingLocator, defaultValue) {
        let ecsSetting;
        let ecsSettingToReturn;
        // Getting the setting from the inMemory setting map.
        if ((ecsSetting = this.previouslyAccessedEcsSettings.get(settingLocator.stringifiedLocator))) {
            return ecsSetting;
        }
        // If we don't find it in the current inMemory setting map, read from other sources
        if ((ecsSetting = getSettingOverrideFromQueryParam(settingLocator))) {
            ecsSettingToReturn = ecsSetting;
        }
        else if ((ecsSetting = getSettingOverrideFromLocalStorage(settingLocator))) {
            ecsSettingToReturn = ecsSetting;
        }
        else if ((ecsSetting = this.getSettingFromCachedEcsResponse(settingLocator))) {
            ecsSettingToReturn = ecsSetting;
        }
        else {
            ecsSettingToReturn = {
                settingValue: defaultValue,
                ecsSettingSource: EcsSettingSource.Default
            };
        }
        // Storing the setting we retrieved in the memory so we always return the previously accessed value
        this.previouslyAccessedEcsSettings.set(settingLocator.stringifiedLocator, ecsSettingToReturn);
        return ecsSettingToReturn;
    }
    /**
     * Gets setting from `EcsResponse` stored in cache.
     * @returns the value and source of the specified setting or
     * `undefined` if cache doesn't exist or setting is not found
     */
    getSettingFromCachedEcsResponse(setting) {
        const settingFromEcsResponse = this.ecsResponseFromCache?.ecsTeamsSettings[setting.teamName] &&
            // Exception added when eslint rule was added, this should be revisited when modifying this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            this.ecsResponseFromCache?.ecsTeamsSettings[setting.teamName][setting.settingName];
        if (settingFromEcsResponse !== undefined) {
            const ecsSetting = {
                settingValue: settingFromEcsResponse,
                ecsSettingSource: EcsSettingSource.CachedServiceResponse
            };
            // Checking for undefined value and not truthy because an empty string and 0 are valid setting values
            // Exception added when eslint rule was added, this should be revisited when modifying this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (ecsSetting.settingValue !== undefined) {
                return ecsSetting;
            }
        }
        return undefined;
    }
    /**
     * Fetch response from the ECS service and return the response parsed as a `EcsResponse`.
     * @param ecsFetchUrl Url to used to fetch from ECS.
     * @returns A promise that, when resolved, returns the `EcsResponse` from ECS.
     */
    async fetchAndParse(ecsFetchUrl) {
        // Starting activity to measure the network call and be able to have a success rate for ECS calls.
        const ecsFetchTracker = ActivityTracker.start('EcsFetch', this.logger, {
            addUserTiming: true
        });
        let response;
        try {
            response = await fetchWithRetry(ecsFetchUrl, undefined, ecsClientRetryPolicy);
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any
      -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
        }
        catch (error) {
            ecsFetchTracker.setResult(false, {}, error);
            // Making sure fetchAndParse() doesn't throw
            return undefined;
        }
        if (response.result.status !== 200) {
            const ecsError = new Error(`Failed to Fetch from ECS. Response is not 200`);
            ecsFetchTracker.setResult(false, {
                status: response.result.status,
                statusText: response.result.statusText
            }, ecsError);
            return undefined;
        }
        const parsingPromise = this.parseResponseToEcsResponse(response);
        parsingPromise
            .then((ecsResponse) => {
            // Getting the actual network call duration.
            let networkDurationMs;
            performance.getEntriesByName(ecsFetchUrl).map((entry) => {
                if (entry.name.includes(this.ecsClientConfiguration.host)) {
                    // Gets last call to `this.ecsClientConfiguration.host` added to performance entries.
                    networkDurationMs = entry.duration;
                }
            });
            ecsFetchTracker.setResult(true, {
                eTag: ecsResponse.eTag,
                networkDurationMs,
                configIds: JSON.stringify(ecsResponse.configIDs)
            });
        })
            .catch((error) => {
            ecsFetchTracker.setResult(false, {}, error);
        });
        return parsingPromise;
    }
    /**
     * Parses a response to a `EcsResponse` that the `EcsClient` understands.
     * @param response response from ECS.
     * @returns an `EcsResponse` that represents the fetch from Ecs.
     */
    async parseResponseToEcsResponse(response) {
        const responseJson = await response.result.json();
        const ecsTeams = [...this.ecsClientConfiguration.teamNames];
        const ecsTeamsSettings = {};
        this.ecsClientConfiguration.guestAgents?.forEach((ecsTeam) => {
            // removing trailing parentheses and clientName
            if (guestAgentPattern.test(ecsTeam)) {
                const ecsTeamNameWithoutClient = ecsTeam.substring(0, ecsTeam.indexOf('('));
                ecsTeams.push(ecsTeamNameWithoutClient);
            }
            else {
                this.logger &&
                    sendErrorEvent(this.logger, {
                        eventName: 'ecsTeamFormat'
                    }, new Error(`${ecsTeam} doesn't match the format <EcsTeam>(<EcsClient>)`));
            }
        });
        for (let i = 0; i < ecsTeams.length; i += 1) {
            if (responseJson[ecsTeams[i]]) {
                ecsTeamsSettings[ecsTeams[i]] = responseJson[ecsTeams[i]];
            }
        }
        const eTag = response.result.headers.get('ETag') || '';
        const configIDs = responseJson.ConfigIDs || {};
        let expires;
        // if override exist, use override to set the expires Date, if not, use response header.
        if (this.ecsClientConfiguration.cacheExpiresDurationOverrideInMin) {
            expires = new Date(Date.now() +
                this.ecsClientConfiguration.cacheExpiresDurationOverrideInMin * 60000 /* 60000 to turn min into ms */);
        }
        else {
            const expiresHeader = response.result.headers.get('Expires');
            expires = expiresHeader ? new Date(expiresHeader) : undefined;
        }
        return {
            ecsTeamsSettings,
            eTag,
            configIDs,
            expires,
            cacheVersion,
            sessionId: this.ecsClientConfiguration.sessionId
        };
    }
    /**
     * Stores `EcsResponse` in `LocalStorage` cache.
     * @param ecsResponse `EcsResponse` retrieved from the ECS Service.
     */
    cacheEcsResponse(ecsResponse) {
        const localStorage = safeLocalStorage();
        if (!localStorage) {
            return;
        }
        try {
            // We took the decision to store multiple `EcsResponses`, so we can handle the case of different cacheKeys.
            const cachedEcsResponses = this.getAllCachedEcsResponses() || {};
            cachedEcsResponses[this.ecsClientConfiguration.cacheKey] = ecsResponse;
            localStorage.setItem(this.ecsClientConfiguration.responsesCacheKey, JSON.stringify(cachedEcsResponses));
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any
      -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
        }
        catch (error) {
            this.logger &&
                sendErrorEvent(this.logger, {
                    eventName: 'cacheEcsResponse'
                }, error);
        }
    }
    /**
     * Gets all `EcsResponse` objects stored in `LocalStorage` cache.
     * It is possible to have more than one response stored in cache if the `cacheKey` is different.
     * e.g. We can have multiple accounts simultaneously loading fluid content and that could end up in multiple responses
     *      being stored in cache.
     * @returns An object with all `EcsResponse` objects stored in cache. `undefined` if couldn't find any.
     */
    getAllCachedEcsResponses() {
        try {
            const localStorage = safeLocalStorage();
            const cachedEcsResponses = localStorage?.getItem(this.ecsClientConfiguration.responsesCacheKey);
            const parsedCurrentCachedResponses = cachedEcsResponses && JSON.parse(cachedEcsResponses);
            // Exception added when eslint rule was added, this should be revisited when modifying this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!parsedCurrentCachedResponses || Object.keys(parsedCurrentCachedResponses).length === 0) {
                return undefined;
            }
            // we need to parse the date due to JSON.parse returning a string instead of a date
            // doing it for all the responses because we use the expires property for cleaning.
            Object.entries(parsedCurrentCachedResponses).forEach(([_key, cachedEcsResponse]) => {
                if (typeof cachedEcsResponse.expires === 'string') {
                    cachedEcsResponse.expires = new Date(cachedEcsResponse.expires);
                }
            });
            return parsedCurrentCachedResponses;
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any
      -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
        }
        catch (error) {
            this.logger &&
                sendErrorEvent(this.logger, {
                    eventName: 'getAllCachedEcsResponses'
                }, error);
        }
        return undefined;
    }
    /**
     * Loads an `EcsResponse` to memory if there is one in the cache that matches `this.ecsClientConfiguration.cacheKey`
     * in `LocalStorage` and clears expired `EcsResponses` from LocalStorage
     */
    loadAndClearCache() {
        // Retrieving all the `EcsResponse` objects from `LocalStorage` cache
        const cachedEcsResponses = this.getAllCachedEcsResponses();
        if (cachedEcsResponses) {
            let cachedEcsResponse = cachedEcsResponses[this.ecsClientConfiguration.cacheKey];
            // Store an `EcsResponse` in memory if there is one for the current `this.ecsClientConfiguration.cacheKey`
            // Exception added when eslint rule was added, this should be revisited when modifying this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (cachedEcsResponse) {
                this.ecsResponseFromCache = cachedEcsResponse;
                // Logging here to know when we loaded from Cache.
                this.logger &&
                    sendTelemetryEvent(this.logger, {
                        eventName: 'loadedEcsResponseFromCache',
                        settings: JSON.stringify(this.ecsResponseFromCache.ecsTeamsSettings),
                        eTag: this.ecsResponseFromCache.eTag,
                        configIds: JSON.stringify(this.ecsResponseFromCache.configIDs)
                    });
            }
            // Clearing the cache after doing all the load,
            // this means that `this.ecsResponseFromCache` could be expired
            this.clearExpiredEcsResponsesFromCache(cachedEcsResponses);
        }
    }
    /**
     * Clears expired `EcsResponse` objects from cache to keep it as clean as possible.
     * We say an `EcsResponse` is expired whenever the expires date is before the current time
     * or the `cacheVersion` is older than the current one.
     * @param cachedEcsResponses `EcsResponse` objects retrieved from `LocalStorage` cache.
     */
    clearExpiredEcsResponsesFromCache(cachedEcsResponses) {
        try {
            const localStorage = safeLocalStorage();
            // return if `LocalStorage` is not defined
            if (!localStorage) {
                return;
            }
            // Creating a new object that is meant to replace the current cache.
            let notExpiredEcsResponses = {};
            // Iterate through responses and store the ones that are not expire.
            Object.entries(cachedEcsResponses).forEach(([key, cachedEcsResponse]) => {
                // We are also storing the `EcsResponse` for the current `EcsClient` ignoring expiration for the next reasons:
                //  1. If the current cached `EcsResponse` is expired, we are going to kick off a network request to update it.
                //  2. In the situation of creating multiple `EcsSettingsProvider` in a short time, if we don't do this,
                //     the first one to clear the cache is going to delete the expired cache for the other
                //     `EcsSettingsProvider` which are trying to read the same `EcsResponse`.
                //  3. If the network request that is meant to update the expired cache doesn't succeed,
                //     we are going to end up deleting the cache for following loads.
                if (ecsResponseIsStillValid(cachedEcsResponse) || key === this.ecsClientConfiguration.cacheKey) {
                    notExpiredEcsResponses[key] = cachedEcsResponse;
                }
            });
            // Write responses into `LocalStorage`
            localStorage.setItem(this.ecsClientConfiguration.responsesCacheKey, JSON.stringify(notExpiredEcsResponses));
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any
      -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
        }
        catch (error) {
            this.logger &&
                sendErrorEvent(this.logger, {
                    eventName: 'clearExpiredEcsResponsesFromCache'
                }, error);
        }
    }
}
//# sourceMappingURL=EcsClient.js.map